/**
 * A strongly-typed value option ID.
 */
export declare type ValueOptionId = number;

/**
 * Represents the value option entity.
 */
export interface ValueOption {
  /**
   * Gets the value option ID.
   */
  readonly id: ValueOptionId;

  /**
   * Gets the value option name.
   */
  name: string;

  /**
   * Gets the value option data type.
   */
  dataType: string;

  /**
   * Gets the value option items.
   */
  values: ValueOptionItem[];

  /**
   * Gets the value option type.
   */
  type: ValueOptionType;

  /**
   * Gets the value option dynamicOptions
   */
  dynamicOptions: DynamicOption;

  /** Gets the date the value option was created.
   */
  readonly created: string;

  /**
   * Gets the date the value option was last updated.
   */
  readonly updated: string;
}

export interface ValueOptionItem {
  /**
   * The value option value key.
   */
  key: string;

  /**
   * The value option value.
   */
  value: string;
}

export enum ValueOptionType {
  StaticValues = 1,
  DynamicValues = 2,
}

export interface DynamicOption {
  /**
   * Gets the value option database connection ID.
   */
  databaseConnectionID: number;

  /**
   * Gets the value option table name.
   */
  tableName: string;

  /**
   * Gets the value option label.
   */
  label: string;

  /**
   * Gets the value option value.
   */
  value: string;

  /**
   * Gets the value option useModelName.
   */
  useModelName: boolean;

  /**
   * Gets the value option useScenarioName.
   */
  useScenarioName: boolean;

  /**
   * Gets the value option model.
   */
  model: string;

  /**
   * Gets the value option scenario.
   */
  scenario: string;

  /**
   * Gets the value option staticFilter.
   */
  staticFilter: string;

  /**
   * Gets the value option orderBy.
   */
  orderBy: string | null;
}

/**
 * Request payload used to create a value option.
 */
export interface CreateValueOptionPayload {
  /**
   * The value option name.
   */
  name: string;

  /**
   * The value option data type.
   */
  dataType: string;

  /**
   * Gets the value option type.
   */
  type: number;

  /**
   * Gets the value option dynamicOptions
   */
  dynamicOptions: DynamicOption;

  /**
   * The value option values.
   */
  values: ValueOptionItem[];
}

/**
 * Request payload used to update a value option.
 */
export type UpdateValueOptionPayload = CreateValueOptionPayload;
